// chatContext/currentAppContextEnvelope.ts
//
// The seam between the envelope hook and `sendChatTurn`. Mounted chat shells
// publish their envelope here; the thunk reads the top of the stack for any
// turn whose caller did not pass one. That way composer, preset chips,
// follow-ups, retry and selection prefill all put the same context on the wire
// without each send path having to thread it, and a caller that wants a
// different envelope (ORA-447's composer, a replay, a test) still wins.
//
// It is a stack rather than a single slot because more than one shell can be
// mounted at once — the global drawer lives in `ProtectedLayout` and the chat
// page is a route under it. The most recently published *visible* surface wins;
// when it retracts (it is hidden, or it unmounts) the surface underneath
// resumes, instead of the app being left with no context at all.

import type { ChatAppContextEnvelope } from '../types/chatEnvelope';
import { registerChatSessionClearer } from '../slices/chat/chatSessionLifecycle';
import { captureScreenSnapshot } from './screenSnapshot';
import { isChatSurfaceAnchorClaimed } from './chatAppContextStore';

interface PublishedEnvelope {
  /** Identifies one hook instance for the life of its component. */
  publisherId: string;
  envelope: ChatAppContextEnvelope;
  /**
   * The publishing surface's chat gate (`isChatAvailable`). It travels with the
   * envelope because the send path — not the render path — decides what a turn
   * may carry, and a live self-assessment must be able to veto it there.
   */
  isChatAvailable: boolean;
}

const stack: PublishedEnvelope[] = [];

const indexOfPublisher = (publisherId: string): number =>
  stack.findIndex((entry) => entry.publisherId === publisherId);

/**
 * Publish (or re-publish) one surface's envelope, moving it to the top. Called
 * by `useAppContextEnvelope` from every visible surface.
 */
export const publishAppContextEnvelope = (
  publisherId: string,
  envelope: ChatAppContextEnvelope,
  isChatAvailable: boolean
): void => {
  const existing = indexOfPublisher(publisherId);

  if (existing !== -1) stack.splice(existing, 1);

  stack.push({ publisherId, envelope, isChatAvailable });
};

/** Withdraw one surface: it went invisible, or it unmounted. */
export const retractAppContextEnvelope = (publisherId: string): void => {
  const existing = indexOfPublisher(publisherId);

  if (existing !== -1) stack.splice(existing, 1);
};

/** The envelope `sendChatTurn` falls back to. Null before any shell mounts. */
export const getCurrentAppContextEnvelope =
  (): ChatAppContextEnvelope | null =>
    stack.length === 0 ? null : stack[stack.length - 1].envelope;

/**
 * Attach the screen outline (ORA-629). Called once per send, from
 * `sendChatTurn` — never from a render: the walk is cheap but not free, and
 * what the student is looking at is only meaningful at the moment they ask.
 *
 * Three conditions, all of which must hold:
 *
 * - The surface has a page behind it to read. The chat page's own thread is the
 *   whole screen. During a View Document session (ORA-736) the companion
 *   covers the page with the lecture, which the envelope already names; the
 *   page beneath is not what the student is asking about.
 * - Chat has to be available. During a live self-assessment nothing about the
 *   page may go to the model, so an absent publisher counts as unavailable —
 *   the same fail-closed reading `deriveAppContextEnvelope` applies.
 * - The capture has to have found something worth sending.
 * - The student has not removed the context pill (ORA-662). Detaching drops
 *   the anchor; leaving the page text attached would hand the same question
 *   back in prose, through the side door.
 */
export const withScreenSnapshot = <
  TEnvelope extends ChatAppContextEnvelope | Record<string, unknown>,
>(
  envelope: TEnvelope
): TEnvelope => {
  // Widened because the wire request types the envelope as a bare record: a
  // caller may hand one straight through without it passing through the hook.
  const surface = (envelope as ChatAppContextEnvelope).surface;

  if (surface === 'chat_page' || isChatSurfaceAnchorClaimed()) return envelope;
  if ((envelope as ChatAppContextEnvelope).anchorDetached === true)
    return envelope;

  const top = stack[stack.length - 1];

  if (!top?.isChatAvailable) return envelope;

  const screen = captureScreenSnapshot();

  return screen ? { ...envelope, screen } : envelope;
};

/** Test helper: forget every publisher. */
export const resetAppContextEnvelopes = (): void => {
  stack.length = 0;
};

registerChatSessionClearer(resetAppContextEnvelopes);
